import React, { useState, useEffect, ChangeEvent } from "react";
import { toast } from "react-toastify";

import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { fetchGiftcardProviders } from "../../../../features/auth/store/giftcardProviderSlice";
import { fetchGiftcardCategories } from "../../../../features/auth/store/giftcardCategorySlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";
import Modal from "../../../../components/Reusable/Modal";
import { Giftcard } from "../../Giftcards/Plans/types";
import {
  ReferralSettingsFields,
  referralEligibilityToFormValue,
} from "../ReferralSettingsFields";
import {
  updateGiftcardPlan,
  fetchGiftcardPlans,
} from "../../../../features/auth/store/giftcardPlanSlice";

type EditGiftCardProps = {
  isOpen: boolean;
  onClose?: () => void;
  giftCardPlan?: Giftcard;
};

export const EditGiftCardModal: React.FC<EditGiftCardProps> = ({
  isOpen,
  onClose,
  giftCardPlan,
}) => {
  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    name: "",
    message: "",
    code: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    status: "on",
    source: "",
    out_of_stock: false,
    countries: [] as string[],
    discount: "",
    referral_eligible: "0",
    referral_amount: "",
    point_bonus: "",
    price_type: "fixed",
    price_currency_id: "",
    // category: "",
    index: 1,
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined,
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false,
  );
  // Providers and categories from redux
  const { giftcardProviders } = useAppSelector(
    (state) => state.giftcardProviders,
  );
  const { giftcardCategories } = useAppSelector(
    (state) => state.giftcardCategories,
  );
  const { currentPage } = useAppSelector((state) => state.giftcardPlans);
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);

  const deleteOldImage =
    giftCardPlan?.image_url !== null && currentImage === undefined;

  // Fetch providers on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchGiftcardProviders({ perPage: 200 }));
      dispatch(fetchCurrencies({ perPage: 200 }));
      dispatch(fetchCountries({ perPage: 200 }));
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (giftCardPlan) {
      let countries = [] as string[];
      if (giftCardPlan.countries) {
        const isString = giftCardPlan.countries.every(
          (country) => typeof country === "string",
        );
        countries = isString
          ? (giftCardPlan.countries as string[])
          : giftCardPlan.countries.map((country) => String(country));
      }

      setcurrentImage(giftCardPlan.image_url);

      setFormData({
        provider_id: giftCardPlan.category?.provider_id?.toString() || "",
        sub_category_id: giftCardPlan.category_id?.toString() || "",
        wallet_price: giftCardPlan.wallet_price?.toString() || "",
        api_price: giftCardPlan?.api_price?.toString() || "",
        atm_price: giftCardPlan?.atm_price?.toString() || "",
        discount: giftCardPlan.discount?.toString() || "",
        out_of_stock: Boolean(giftCardPlan.out_of_stock),
        referral_eligible: referralEligibilityToFormValue(
          giftCardPlan.referral_eligible,
        ),
        referral_amount: giftCardPlan.referral_amount?.toString() || "",
        point_bonus: giftCardPlan.point_bonus?.toString() || "",
        status: giftCardPlan.status,
        source: giftCardPlan.source,
        code: giftCardPlan.code || "",
        price_type: giftCardPlan.price_type || "fixed",
        price_currency_id: giftCardPlan.price_currency_id?.toString() || "",
        name: giftCardPlan.name || "",
        message: giftCardPlan.message,
        index: giftCardPlan.index || 1,
        countries,
      });
    }
  }, [giftCardPlan]);

  // Fetch categories when provider changes
  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchGiftcardCategories({ perPage: 200 }));
    }
  }, [dispatch, formData.provider_id]);

  // Filter categories for selected provider
  useEffect(() => {
    if (formData.provider_id && giftcardCategories.length) {
      setFilteredSubCategories(
        giftcardCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id),
        ),
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, giftcardCategories]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      setFormData({ ...formData, [e.target.name]: e.target.checked });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}), // reset subcategory if provider changes
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const countryIds = countries.map((country) => String(country.id));
  const allCountriesSelected =
    countryIds.length > 0 &&
    countryIds.every((id) => formData.countries.includes(id));

  const toggleAllCountries = () => {
    setFormData((prev) => {
      const shouldDeselectAll = countryIds.every((id) =>
        prev.countries.includes(id),
      );
      return {
        ...prev,
        countries: shouldDeselectAll ? [] : countryIds,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!giftCardPlan) return;

    const formDataObj = new FormData();

    // Append all required fields
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id);
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message || "");
    formDataObj.append("wallet_price", formData.wallet_price || "0");
    formDataObj.append("api_price", formData.api_price || "0");
    formDataObj.append("atm_price", formData.atm_price || "0");
    formDataObj.append("status", formData.status);
    formDataObj.append("source", formData.source);
    formDataObj.append("out_of_stock", formData.out_of_stock ? "1" : "0");
    // formDataObj.append("category", formData.category);
    formDataObj.append("discount", formData.discount || "0");
    formDataObj.append("referral_eligible", formData.referral_eligible);
    formDataObj.append("referral_amount", formData.referral_amount || "0");
    formDataObj.append("point_bonus", formData.point_bonus);
    formDataObj.append("code", formData.code || "");
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c))),
    );
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    formDataObj.append("index", String(formData.index || 1));
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }

    if (image) {
      formDataObj.append("image", image);
    }
    const filters: any = { page: currentPage };
    try {
      await dispatch(
        updateGiftcardPlan({ id: giftCardPlan.id, data: formDataObj }),
      ).unwrap();
      toast.success("Gift card plan updated successfully!");
      handleClose();
      await dispatch(fetchGiftcardPlans(filters)); // Refresh the list after creation
    } catch (error: any) {
      toast.error(error?.message || "Failed to update gift card plan");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Reset form
    setFormData({
      provider_id: "",
      sub_category_id: "",
      name: "",
      message: "",
      wallet_price: "",
      api_price: "",
      atm_price: "",
      status: "on",
      source: "",
      out_of_stock: false,
      countries: [],
      code: "",
      // category: "",
      discount: "",
      referral_eligible: "0",
      referral_amount: "",
      point_bonus: "",
      price_type: "fixed",
      price_currency_id: "",
      index: 1,
    });
    setcurrentImage(undefined);
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto"
    >
      <div className="max-h-[90vh] overflow-y-auto pr-1">
        <h2 className="text-lg md:text-xl font-bold mb-6">
          Gift Card Plan Details
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Provider */}
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...giftcardProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          {/* Sub Category */}
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          {/* Status */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              placeholder="Status"
              options={[
                { value: "on", label: "Active" },
                { value: "off", label: "Inactive" },
              ]}
            />
          </div>

          <div className="relative mt-2 flex items-center h-12 w-full border border-inputBorder px-3.5 dark:bg-white/10 rounded-lg focus-within:border-black">
            <label
              htmlFor="out_of_stock"
              className="absolute -top-2.5 left-3.5 px-1 text-sm bg-white dark:bg-[#1C1C1E] satoshi-sm-medium-black dark:text-white pointer-events-none"
            >
              Out of Stock
            </label>
            <div className="flex items-center gap-2.5 w-full cursor-pointer">
              <input
                id="out_of_stock"
                type="checkbox"
                name="out_of_stock"
                checked={formData.out_of_stock}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black accent-black dark:bg-gray-800 dark:border-gray-600 cursor-pointer"
              />
              <label
                htmlFor="out_of_stock"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none"
              >
                {formData.out_of_stock
                  ? "Yes (Marked as Out of Stock)"
                  : "No (In Stock)"}
              </label>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="name"
              placeholder="Product Name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>
          {/* Product Category */}
          {/* <div>
            <FloatingLabelSelect
              name="category"
              placeholder="Product Category"
              value={formData.category}
              onChange={handleInputChange}
              options={[
                { value: "prePaid", label: "Prepaid" },
                { value: "postPaid", label: "Postpaid" },
              ]}
            />
          </div> */}
          {/* Price Currency */}
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Price Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
          />
          {/* Price Type */}
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="wallet_price"
              placeholder="Wallet Price"
              value={formData.wallet_price}
              onChange={handleInputChange}
            />
          </div>
          {/* API Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="api_price"
              placeholder="API Price"
              value={formData.api_price}
              onChange={handleInputChange}
            />
          </div>
          {/* ATM Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="atm_price"
              placeholder="ATM Price"
              value={formData.atm_price}
              onChange={handleInputChange}
            />
          </div>
          {/* Point Bonus */}
          <LabelInput
            type="number"
            name="point_bonus"
            placeholder="Point Bonus"
            value={formData.point_bonus}
            onChange={handleInputChange}
          />
          {/* Discount Offered  */}
          <LabelInput
            type="number"
            name="discount"
            placeholder="Discount Offered"
            value={formData.discount}
            onChange={handleInputChange}
          />
          <ReferralSettingsFields
            referralEligible={formData.referral_eligible}
            referralAmount={formData.referral_amount}
            priceType={formData.price_type}
            onChange={handleInputChange}
          />
          {/* Source */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="source"
              placeholder="Source"
              value={formData.source}
              onChange={handleInputChange}
            />
          </div>

          {/* Code */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="code"
              placeholder="Code (Optional)"
              value={formData.code || ""}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="index"
              placeholder="Index"
              value={String(formData.index)}
              onChange={handleInputChange}
            />
          </div>

          {/* Countries */}
          <h3 className="text-lg font-semibold text-gray-800 mb-2 dark:text-white">
            Countries (availability)
          </h3>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
            {countriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : countries.length === 0 ? (
              <p className="text-sm text-gray-500">
                No countries found. Create countries first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 border-b pb-2 mb-1">
                  <input
                    type="checkbox"
                    checked={allCountriesSelected}
                    onChange={toggleAllCountries}
                  />
                  <span>Select all countries</span>
                </label>
                {countries.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(c.id))}
                      onChange={() => toggleCountry(c.id)}
                    />
                    <span>
                      {c.name} ({c.symbol})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Product Images */}
          <div className="space-y-4">
            <p className="text-base font-semibold">Product Images</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
                multiple
              />
              <label
                htmlFor="fileUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="product"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Description */}
          <div className="space-y-2">
            <p className="text-sm font-medium mb-1">Description</p>
            <textarea
              name="message"
              placeholder="Description"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-300 hover:bg-gray-400"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-6 py-2.5 sm:px-8 sm:py-2 w-full sm:w-[150px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bgColor={"bg-gradient-to-r from-[#027EC2] to-[#039EF2]"}
              textColor="text-white"
              borderRadius="rounded-lg"
              className="px-6 py-2.5 sm:px-8 sm:py-2 w-full sm:w-[150px]"
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
